import React from "react";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet";

import Layout from "../templates/siteLayout";
import Seo from "../components/seo";
import BlogPost from "../components/blogPost";

import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  LinkedinShareButton,
  LinkedinIcon,
  RedditShareButton,
  RedditIcon,
  EmailShareButton,
  EmailIcon,
} from "react-share";

import { baseColor1, baseColor2, inputDefaultColor } from "../styles/color";
import { breakpointMd } from "../styles/layout";
import { boxShadow3 } from "../styles/common";

const heroStyles = css`
  position: relative;
  top: 3rem;
  width: 100%;
  border-radius: 0.5rem;
  box-shadow: ${boxShadow3};
`;

const headerStyles = css`
  #metadata {
    margin-top: 5rem;
  }

  #title {
    margin: 0.5rem 0 1rem 0;
    font-size: 2.5rem;
  
    @media only screen and (min-width: ${breakpointMd}) {
      font-size: 3.25rem;
    }
  }

  #byline {
    margin-top: 1.25rem;
    display: grid;
    align-items: center;
    grid-template-columns: 48px auto;
    gap: 0.75rem;

    img {
      width: 48px;
      height: 48px;
      border-radius: 50%;
    }

    span {
      font-weight: 600;
    }
  }
`;

const ShareButtons = styled.span`
  display: grid;
  gap: 2rem;
  align-items: center;
  border-radius: 16px;
  color: ${baseColor2};

  .separator {
    height: 1px;
    background-color: ${inputDefaultColor};
  }

  .icons {
    justify-self: center;
    border-radius: 16px;
    display: inline-flex;
    gap: 0.7rem;
    align-items: center;
    background-color: ${baseColor1};
  }

  button {
    height: 32px;
  }
`;

const BlogPostTemplate = ({ data }) => {
  const post = data.sanityPost;
  const shareUrl = `https://onework.co/blog/${post.slug.current}`;
  const structuredDataDate = new Date(post.publishedAt).toISOString();

  return (
    <Layout>
      <Seo
        title={post.title}
        description={post.excerpt}
        image={`${post.mainImage.asset.url}?w=800&h=450`}
      />
      {/* Structured data to support SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: post.title,
            image: [
              `${post.mainImage.asset.url}?w=550&h=550&fit=min`,
              `${post.mainImage.asset.url}?w=668&h=501&fit=min`,
              `${post.mainImage.asset.url}?w=800&h=450&fit=min`,
            ],
            datePublished: structuredDataDate,
            dateModified: structuredDataDate,
          })}
        </script>
      </Helmet>

      <div className="material-section">
        <section className="grid-item-100">
          <GatsbyImage
            css={heroStyles}
            image={post.mainImage.asset.gatsbyImageData}
            alt={post.mainImage.alt || "alt text"}
          />
        </section>
      </div>
      <div className="white-bg">
        <div className="material-section">
          <section css={headerStyles} className="grid-item-100">
            <div id="metadata">
              {post.categories[0].title || "News"} • {post.publishedAt}
            </div>
            <h1 id="title">{post.title}</h1>
            <div id="byline">
              <img
                src={`${post.author.image.asset.url}?w=100&h=100`}
                alt={post.author.name}
              />
              <div>
                Written by <span>{post.author.name}</span>
              </div>
            </div>
          </section>
        </div>
        <div className="material-section py-2">
          <article className="grid-item-75-right">
            {/* Blog content based on the Sanity.io starter with Gatsby */}
            <BlogPost {...post} />
          </article>

          <section className="grid-item-75-right">
            <ShareButtons>
              <div className="separator"></div>

              <div className="icons">
                <FacebookShareButton
                  url={shareUrl}
                  className="facebook-share-button"
                >
                  <FacebookIcon size={32} round />
                </FacebookShareButton>
                <TwitterShareButton
                  url={shareUrl}
                  title={post.title}
                  via="joinonework"
                  className="twitter-share-button"
                >
                  <TwitterIcon size={32} round />
                </TwitterShareButton>
                <LinkedinShareButton
                  url={shareUrl}
                  title={post.title}
                  source="One Work"
                  className="linkedin-share-button"
                >
                  <LinkedinIcon size={32} round />
                </LinkedinShareButton>
                <RedditShareButton
                  url={shareUrl}
                  title={post.title}
                  className="reddit-share-button"
                >
                  <RedditIcon size={32} round />
                </RedditShareButton>
                <EmailShareButton
                  url={shareUrl}
                  subject="👋 I thought you'd like this blog post from One Work"
                  body="Here's the link — let me know what you think!"
                  className="email-share-button"
                >
                  <EmailIcon size={32} round />
                </EmailShareButton>
              </div>
            </ShareButtons>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default BlogPostTemplate;

export const query = graphql`
  query ($id: String!) {
    sanityPost(id: { eq: $id }) {
      title
      slug {
        current
      }
      excerpt
      author {
        name
        image {
          asset {
            url
          }
        }
        bio {
          children {
            text
          }
        }
      }
      mainImage {
        asset {
          url
          gatsbyImageData(placeholder: BLURRED)
        }
        alt
        caption
      }
      categories {
        title
      }
      publishedAt(formatString: "MMMM D, YYYY")
      _rawBody
    }
  }
`;
